import React, { useState, useEffect, useRef } from 'react';
import { MoreHorizontal, Star, Forward } from 'lucide-react';
import MessageBubble from './MessageBubble';
import MessageInfoPopover from './MessageInfoPopover';
import DeliveryIndicator from './DeliveryIndicator';
import MessageActionsMenu from './MessageActionsMenu';

const MessageItem = React.memo(
  ({
    message,
    selectedUser,
    c,
    isRTL,
    renderMessageContent,
    employeeProfilePics,
    getAvatarColor,
    styles,
    showAvatar,
    t,
    language,
    theme,
    onDeleteMessage,
    // WhatsApp-style actions
    onReply,
    onForward,
    onToggleStar,
    isStarred,
    onReact,
    canViewDetails = true,
    currentUserCode,
    resolveReactorName,
    onJumpToMessage,
    // Not used directly — drives the memo comparator so search highlighting
    // re-renders the bubbles (renderMessageContent closes over it).
    // eslint-disable-next-line no-unused-vars
    searchTerm,
    // Smaller layout for the Mini Chat Dock's narrow floating window.
    compact = false,
    // Hides the received-message avatar entirely (Mini Chat Dock's 1:1
    // windows) — bubbles just align to one side, WhatsApp/Telegram-style,
    // instead of reserving space for an avatar that's never shown.
    hideAvatar = false,
  }) => {
    const [showInfo, setShowInfo] = useState(false);
    const [showActions, setShowActions] = useState(false);
    const [menuOpensUp, setMenuOpensUp] = useState(false);
    const [menuAnchorRect, setMenuAnchorRect] = useState(null);
    const [isDeletable, setIsDeletable] = useState(false);
    const bubbleAreaRef = useRef(null);

    useEffect(() => {
      if (!message.sent) return;
      const ts = message.raw?.created_at || message.raw?.createdAt || message.raw?.time;
      const messageTime = ts ? new Date(ts).getTime() : Date.now(); // default to now if missing for temp messages
      if (!messageTime || isNaN(messageTime)) return;

      const check = () => {
        setIsDeletable(Date.now() - messageTime < 5 * 60 * 1000);
      };
      check();
      const interval = setInterval(check, 10000);
      return () => clearInterval(interval);
    }, [message]);

    // Clean up time string if it contains status text accidentally injected
    const cleanTime = message.time ? message.time.replace(/تم الإرسال|تم التسليم|تم القراءة|Sent|Delivered|Read|Seen/g, '').trim() : '';

    const openActions = () => {
      // Open the menu upward when the bubble sits in the lower half of the
      // viewport so it doesn't clip below the scroll container. The rect is
      // also handed to the portal-rendered menu so it can position itself in
      // viewport coordinates instead of relying on CSS position:absolute,
      // which would get clipped by any overflow:hidden/auto ancestor (e.g.
      // the Mini Chat Dock's narrow floating window).
      const rect = bubbleAreaRef.current?.getBoundingClientRect();
      setMenuOpensUp(!!rect && rect.top > window.innerHeight * 0.55);
      setMenuAnchorRect(rect || null);
      setShowActions((prev) => !prev);
    };

    const myCode = String(currentUserCode || '');
    const myReaction = message.reactions
      ? Object.keys(message.reactions).find((emoji) =>
          (message.reactions[emoji] || []).some((code) => String(code) === myCode)
        ) || null
      : null;

    const reactionEntries = message.reactions
      ? Object.entries(message.reactions).filter(([, codes]) => Array.isArray(codes) && codes.length > 0)
      : [];

    const replySenderName = message.replyTo
      ? (String(message.replyTo.senderId) === myCode
          ? t.you
          : (resolveReactorName?.(message.replyTo.senderId) || selectedUser.name))
      : null;

    const replyPreviewText = message.replyTo
      ? (message.replyTo.content
          || message.replyTo.mediaName
          || (message.replyTo.type === 'image' ? '📷' : message.replyTo.type === 'voice' ? '🎤' : ''))
      : null;

    const actionsButton = (
      <button
        className="msg-info-btn"
        onClick={(e) => {
          e.stopPropagation();
          openActions();
        }}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '4px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: c.textSecondary,
          flexShrink: 0,
          transition: 'opacity 0.2s ease, color 0.2s ease',
        }}
        title={t.moreOptions}
        aria-label={t.moreOptions}
      >
        <MoreHorizontal size={15} />
      </button>
    );

    // How much horizontal space to reserve on the "other" side of the
    // wrapper so bubbles don't stretch edge-to-edge. With avatars hidden
    // there's no fixed-width avatar to line up against, so a smaller reserve
    // reads cleaner (WhatsApp/Telegram-style) than the full gutter — while
    // still keeping bubbles off the far edge.
    const sideReserve = hideAvatar ? '32px' : (compact ? '12px' : '60px');

    return (
      <div
        className="message-wrapper"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: message.sent ? 'flex-end' : 'flex-start',
          marginBottom: reactionEntries.length > 0 ? (compact ? '12px' : '16px') : (compact ? '5px' : '8px'),
          paddingLeft: message.sent ? (isRTL ? '0' : sideReserve) : (isRTL ? sideReserve : '0'),
          paddingRight: message.sent ? (isRTL ? sideReserve : '0') : (isRTL ? '0' : sideReserve),
          width: '100%',
        }}
      >
        <div
          className="bubble-and-avatar-row"
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: hideAvatar ? '0' : '10px',
            flexDirection: 'row',
            maxWidth: '100%',
          }}
        >
          {/* Avatar (Only for received messages, and only when not hidden) */}
          {!message.sent && !hideAvatar && (
            <div style={{ width: compact ? '28px' : '36px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
              {showAvatar && (() => {
                const isGroup = selectedUser.isGroup || (typeof selectedUser.id === 'string' && selectedUser.id.startsWith('group-'));
                const senderId = isGroup && message.sender?.id ? message.sender.id : selectedUser.id;
                const senderName = isGroup && message.sender?.name ? message.sender.name : selectedUser.name;
                const senderAvatarStr = senderName ? senderName.trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '';

                return (
                  <>
                    {employeeProfilePics[senderId] && (
                      <img
                        src={employeeProfilePics[senderId]}
                        alt={senderName}
                        style={{
                          width: compact ? '28px' : '36px',
                          height: compact ? '28px' : '36px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          border: `2px solid ${c.chatBg}`,
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling?.style) e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    )}
                    <div
                      style={{
                        width: compact ? '28px' : '36px',
                        height: compact ? '28px' : '36px',
                        borderRadius: '50%',
                        display: employeeProfilePics[senderId] ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ffffff',
                        fontSize: compact ? '11px' : '14px',
                        fontWeight: '600',
                        background: getAvatarColor(senderName),
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        border: `2px solid ${c.chatBg}`,
                      }}
                      title={senderName}
                    >
                      {senderAvatarStr}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* Bubble + actions trigger */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: compact ? '4px' : '6px',
              flexDirection: 'row',
              maxWidth: message.sent ? '100%' : (hideAvatar ? '100%' : `calc(100% - ${compact ? '32px' : '46px'})`),
            }}
          >
            {message.sent && actionsButton}

            <div
              ref={bubbleAreaRef}
              style={{ position: 'relative', maxWidth: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: message.sent ? 'flex-end' : 'flex-start' }}
            >
              {!message.sent && (selectedUser.isGroup || (typeof selectedUser.id === 'string' && selectedUser.id.startsWith('group-'))) && showAvatar && message.sender?.name && (
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: c.textSecondary,
                  marginBottom: '2px',
                  marginLeft: isRTL ? '0' : '4px',
                  marginRight: isRTL ? '4px' : '0'
                }}>
                  {message.sender.name.split(' ')[0]}
                </div>
              )}
              <MessageBubble message={message} styles={styles}>
                {/* Forwarded label */}
                {message.isForwarded && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    fontSize: '11.5px', fontStyle: 'italic', opacity: 0.75,
                    marginBottom: '4px', color: 'inherit',
                  }}>
                    <Forward size={12} style={{ transform: isRTL ? 'scaleX(-1)' : 'none' }} />
                    {t.forwarded}
                  </div>
                )}

                {/* Quoted reply preview — click jumps to the original */}
                {message.replyToId && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      if (message.replyTo && onJumpToMessage) onJumpToMessage(message.replyToId);
                    }}
                    style={{
                      borderInlineStart: '3px solid #2e7cba',
                      background: message.sent ? 'rgba(255,255,255,0.15)' : (theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'),
                      borderRadius: '10px',
                      padding: '6px 10px',
                      marginBottom: '6px',
                      cursor: message.replyTo ? 'pointer' : 'default',
                      maxWidth: '100%',
                    }}
                  >
                    {message.replyTo ? (
                      <>
                        <div style={{ fontSize: '11.5px', fontWeight: '700', color: message.sent ? '#dbeafe' : '#2e7cba', marginBottom: '2px' }}>
                          {replySenderName}
                        </div>
                        <div dir="auto" style={{ fontSize: '12.5px', opacity: 0.85, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'inherit' }}>
                          {replyPreviewText}
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: '12.5px', fontStyle: 'italic', opacity: 0.7, color: 'inherit' }}>
                        {t.messageUnavailable}
                      </div>
                    )}
                  </div>
                )}

                {renderMessageContent(message)}
              </MessageBubble>

              {/* Reaction chips */}
              {reactionEntries.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    gap: '4px',
                    flexWrap: 'wrap',
                    marginTop: '-6px',
                    zIndex: 2,
                    [message.sent ? 'marginInlineEnd' : 'marginInlineStart']: '8px',
                  }}
                >
                  {reactionEntries.map(([emoji, codes]) => {
                    const mine = codes.some((code) => String(code) === myCode);
                    const names = codes
                      .map((code) => (String(code) === myCode ? t.you : (resolveReactorName?.(code) || code)))
                      .join('، ');
                    return (
                      <button
                        key={emoji}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!onReact) return;
                          onReact(message, emoji);
                        }}
                        title={names}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px',
                          padding: '2px 7px',
                          borderRadius: '12px',
                          border: `1px solid ${mine ? '#2e7cba' : c.border}`,
                          background: theme === 'dark' ? '#1e293b' : '#ffffff',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                          cursor: onReact ? 'pointer' : 'default',
                          fontSize: '13px',
                          lineHeight: 1.3,
                          color: c.text,
                        }}
                      >
                        <span>{emoji}</span>
                        {codes.length > 1 && (
                          <span style={{ fontSize: '11px', fontWeight: '700', color: mine ? '#2e7cba' : c.textSecondary }}>
                            {codes.length}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              <MessageActionsMenu
                message={message}
                isOpen={showActions}
                onClose={() => setShowActions(false)}
                onShowDetails={canViewDetails ? () => setShowInfo(true) : undefined}
                onCopy={() => {
                  const textToCopy = message.text || message.content || "";
                  if (textToCopy && navigator.clipboard) {
                    navigator.clipboard.writeText(textToCopy).catch(() => {});
                  }
                }}
                onReply={onReply ? () => onReply(message) : undefined}
                onForward={onForward ? () => onForward(message) : undefined}
                onToggleStar={onToggleStar ? () => onToggleStar(message) : undefined}
                isStarred={isStarred}
                onReact={onReact ? (emoji) => onReact(message, emoji) : undefined}
                myReaction={myReaction}
                onDelete={onDeleteMessage ? () => onDeleteMessage(message) : undefined}
                isDeletable={message.sent && isDeletable && !!onDeleteMessage}
                c={c}
                t={t}
                isRTL={isRTL}
                anchorRect={menuAnchorRect}
                opensUp={menuOpensUp}
              />

              {canViewDetails && (
              <MessageInfoPopover
                message={message}
                isOpen={showInfo}
                onClose={() => setShowInfo(false)}
                c={c}
                t={t}
                isRTL={isRTL}
                language={language}
                isDeletable={isDeletable}
                onDeleteMessage={onDeleteMessage}
              />
              )}
            </div>

            {!message.sent && actionsButton}
          </div>
        </div>

        {/* Time & Status Row */}
        {(showAvatar || message.sent) && (() => {
          const isGroup = selectedUser.isGroup || (typeof selectedUser.id === 'string' && selectedUser.id.startsWith('group-'));
          const readBy = message.raw?.readBy || message.raw?.seenBy || [];
          const readCount = Array.isArray(readBy) ? readBy.length : (message.raw?.readCount || 0);

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginTop: compact ? '2px' : '4px',
                paddingInline: compact ? '4px' : 0,
                color: c.textSecondary,
                fontSize: compact ? '10.5px' : '12px',
                marginLeft: !message.sent && !isRTL ? (hideAvatar ? '0' : (compact ? '32px' : '46px')) : '0',
                marginRight: !message.sent && isRTL ? (hideAvatar ? '0' : (compact ? '32px' : '46px')) : '0',
              }}
            >
              {isStarred && <Star size={11} color="#f59e0b" fill="#f59e0b" />}
              <span style={{ margin: 0, direction: 'ltr' }}>{cleanTime}</span>
              {message.sent && <DeliveryIndicator status={message.status} time={null} isGroup={isGroup} readCount={readCount} />}
            </div>
          );
        })()}
      </div>
    );
  },
  (prev, next) =>
    prev.message.id === next.message.id &&
    prev.message.status === next.message.status &&
    prev.message.time === next.message.time &&
    prev.message.readAt === next.message.readAt &&
    prev.message.reactions === next.message.reactions &&
    prev.message.replyTo === next.message.replyTo &&
    prev.isStarred === next.isStarred &&
    prev.showAvatar === next.showAvatar &&
    prev.searchTerm === next.searchTerm &&
    prev.compact === next.compact &&
    prev.hideAvatar === next.hideAvatar &&
    prev.theme === next.theme &&
    prev.isRTL === next.isRTL &&
    prev.language === next.language &&
    prev.c === next.c
);

export default MessageItem;
